import { push } from 'connected-react-router'
import { v4 as uuidv4 } from 'uuid'
import i18n from 'i18next'
import { generatePath } from 'react-router'
import { call, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects'
import {
  AUTORUN_START_TRACK,
  COMPLETE_BRANCH,
  FINALIZE_TRACK,
  COMPLETE_TRACK,
  CONFIRM_INTERRUPT,
  DISMISS_CARD,
  INTERRUPT_TRACK,
  // LOAD_PROGRESS,
  // SAVE_PROGRESS,
  NAVIGATE_TO_URL,
  NEXT_STEP,
  SHOW_ONBOARDING_MODAL,
  START_TRACK,
} from './types'
import { confirmSaga } from 'store/saga-utils/confirm.saga'
import { actionSetData, resolveButtonAction } from './actions'
import { sendOnboardingEvent, trackOnboardingButtonAnalytics } from '../analyticsAdapter'
import {
  activateForkBranch,
  buildCardProgressForTrack,
  buildInitialBranchProgress,
  closeAllOpenBranches,
  collectActiveListeners,
  completeBranch,
  findCurrentCard,
  findPageIdByCardId,
  getFirstPage,
  hasPendingCards,
  markCardAsCompleted,
  skipAllPendingCards,
} from './tree'
import { buildOnboardingCtx } from './ctx'
import { selectOnboardingState } from './selectors'
import { onboardingLog } from './logger'
// TODO(backend): enable when onboarding progress API is ready
// import onboardingApi from 'api/onboarding/v1'
import { createPath } from 'history'
import qs from 'qs'
import { getGoBackPath } from 'utils/createBillingTopUpLink'
// Внешние события, на которые подписываются слушатели онбординга (page.listeners).
import { TOP_UP_SUCCESS } from 'modules/Forms/Billing/types'

/**
 * Resolves navigate params at click time. When `goBackToCurrent` is set, the
 * current location is injected as `goBack` so it reflects the page the user
 * actually came from (cards are static objects evaluated once at import time).
 */
const resolveNavigateParams = (params) => {
  if (!params || !params.goBackToCurrent) {
    return params
  }

  const { goBackToCurrent, ...rest } = params

  return { ...rest, goBack: getGoBackPath() }
}

const resolveCurrentCardCompletion = (onboarding, onBoardingState, payload = {}) => {
  const { cardId: payloadCardId, pageId: payloadPageId } = payload
  const pathname = global.location.pathname
  const currentCard = findCurrentCard(onboarding, pathname, onBoardingState)
  const cardId = currentCard?.cardId || payloadCardId
  const track = onboarding.tracks?.[onboarding.activeTrackName]
  const pageId =
    currentCard?.pageId || payloadPageId || findPageIdByCardId(track, cardId)

  if (!cardId || !pageId) return null

  return {
    cardId,
    pageId,
    cardProgress: markCardAsCompleted(onboarding.cardProgress, pageId, cardId),
  }
}

function* markCurrentCardCompleted(payload = {}) {
  const onboarding = yield select(selectOnboardingState)
  const onBoardingState = yield* selectOnBoardingState()
  const completion = resolveCurrentCardCompletion(onboarding, onBoardingState, payload)

  if (!completion) return null

  const { cardId, pageId, cardProgress } = completion

  onboardingLog('card:state', {
    cardId,
    pageId,
    from: 'pending',
    to: 'completed',
    trackId: onboarding.activeTrackName,
  })

  yield put(actionSetData({ cardProgress }))

  return completion
}

function* getTrack(trackId) {
  const { tracks } = yield select(selectOnboardingState)
  return tracks?.[trackId] || null
}

function* startTrack({ payload }) {
  const { trackId } = payload
  const track = yield* getTrack(trackId)
  if (!track) return

  const onboarding = yield select(selectOnboardingState)
  const onBoardingState = yield* selectOnBoardingState()

  const branchProgress = buildInitialBranchProgress(track, onBoardingState)
  const cardProgress = buildCardProgressForTrack(track, {}, branchProgress, onBoardingState)
  const firstPage = getFirstPage(track.steps, branchProgress, onBoardingState)

  const completedTracks = (onboarding.completedTracks || []).filter((id) => id !== trackId)

  yield put(
    actionSetData({
      activeTrackName: trackId,
      status: 'running',
      cardProgress,
      branchProgress,
      visitFlags: {},
      completedTracks,
    }),
  )

  if (firstPage) {
    const i18nLang = yield select((state) => state.app.i18n)
    yield put(push(generatePath(firstPage.url, { i18n: i18nLang })))
  }
}

function* completeTrack() {
  const onboarding = yield select(selectOnboardingState)
  const completedTracks = [...onboarding.completedTracks]
  const trackId = onboarding.activeTrackName

  if (trackId && !completedTracks.includes(trackId)) {
    completedTracks.push(trackId)
  }

  yield put(
    actionSetData({
      activeTrackName: null,
      status: 'idle',
      completedTracks,
      cardProgress: {},
      branchProgress: {},
      visitFlags: {},
    }),
  )

  const track = yield* getTrack(trackId)
  const completedGoal = track?.analytics?.completed || 'onboarding_completed'
  sendOnboardingEvent(completedGoal, { trackId })
}

function* completeBranchSaga({ payload }) {
  const { branchId } = payload
  if (!branchId) return

  const onboarding = yield select(selectOnboardingState)
  const track = yield* getTrack(onboarding.activeTrackName)
  if (!track) return

  const completion = yield* markCurrentCardCompleted()
  if (!completion) return

  const onBoardingState = yield* selectOnBoardingState()
  const branchProgress = completeBranch(track, branchId, onboarding.branchProgress || {})
  const cardProgress = buildCardProgressForTrack(
    track,
    completion.cardProgress,
    branchProgress,
    onBoardingState,
  )

  yield put(actionSetData({ branchProgress, cardProgress }))

  if (
    !hasPendingCards(track, cardProgress, branchProgress, {
      ...onBoardingState,
      visitFlags: onboarding.visitFlags,
      pathname: global.location.pathname,
    })
  ) {
    yield put({ type: COMPLETE_TRACK })
  }
}

function* finalizeTrack({ payload = {} }) {
  const { branchId } = payload
  const onboarding = yield select(selectOnboardingState)
  const track = yield* getTrack(onboarding.activeTrackName)
  if (!track) return

  const completion = yield* markCurrentCardCompleted()

  const onBoardingState = yield* selectOnBoardingState()
  let branchProgress = onboarding.branchProgress || {}
  let cardProgress = completion?.cardProgress || onboarding.cardProgress || {}

  if (branchId) {
    branchProgress = completeBranch(track, branchId, branchProgress)
    cardProgress = buildCardProgressForTrack(track, cardProgress, branchProgress, onBoardingState)
  }

  branchProgress = closeAllOpenBranches(track, branchProgress, onBoardingState)
  cardProgress = skipAllPendingCards(cardProgress)

  yield put(actionSetData({ branchProgress, cardProgress }))
  yield put({ type: COMPLETE_TRACK })
}

function* nextStep() {
  yield* markCurrentCardCompleted()
}

function* navigateToUrl({ payload }) {
  const { url, route, routeParams, params, cardId, pageId } = payload

  yield* markCurrentCardCompleted({ cardId, pageId })

  const i18nLang = yield select((state) => state.app.i18n)
  let targetPathname = null

  if (url) {
    targetPathname = url
  } else if (route) {
    targetPathname = generatePath(route, { i18n: i18nLang, ...routeParams })
  }

  if (targetPathname) {
    const search = params ? qs.stringify(resolveNavigateParams(params), { arrayFormat: 'brackets' }) : ''
    yield put(push(createPath({ pathname: targetPathname, search })))
  }
}

// Обработчик слушателей онбординга: пассивно реагирует на внешние redux-события
// (например, `BILLING/TOP_UP_SUCCESS`), пока пользователь находится на странице
// активного слушателя. Слушатели объявляются декларативно в конфиге
// (`page.listeners`) и не влияют на прогресс/резолв карточек и завершение —
// см. collectActiveListeners. Связь односторонняя: биллинг ничего не знает про
// онбординг, мы лишь подписываемся на факт.
function* handleOnboardingListenerEvent(action) {
  const onboarding = yield select(selectOnboardingState)
  if (onboarding.status !== 'running') return

  const onBoardingState = yield* selectOnBoardingState()
  const listeners = collectActiveListeners(onboarding, global.location.pathname, onBoardingState)
  if (!listeners.length) return

  for (const listener of listeners) {
    if (listener.on !== action.type) continue
    if (typeof listener.when === 'function' && !listener.when(action)) continue

    if (listener.analyticsGoal) {
      sendOnboardingEvent(listener.analyticsGoal, { trackId: onboarding.activeTrackName })
    }

    if (listener.action) {
      const listenerAction = resolveButtonAction(listener)
      if (listenerAction) yield put(listenerAction)
    }
  }
}

function* interruptTrack() {
  const onboarding = yield select(selectOnboardingState)
  const trackId = onboarding.activeTrackName
  if (!trackId) return

  const track = yield* getTrack(trackId)
  const closedGoal = track?.analytics?.closed || 'onboarding_closed'
  sendOnboardingEvent(closedGoal, { trackId })

  yield put(
    actionSetData({
      activeTrackName: null,
      status: 'idle',
      cardProgress: {},
      branchProgress: {},
      visitFlags: {},
    }),
  )

}

function* dismissCard() {
  const onboarding = yield select(selectOnboardingState)
  const onBoardingState = yield* selectOnBoardingState()
  const pathname = global.location.pathname
  const currentCard = findCurrentCard(onboarding, pathname, onBoardingState)
  const visitFlag = currentCard?.card?.visitFlag

  const completion = resolveCurrentCardCompletion(onboarding, onBoardingState)
  if (!completion) return

  const { cardId, pageId, cardProgress: completedCardProgress } = completion

  onboardingLog('card:state', {
    cardId,
    pageId,
    from: 'pending',
    to: 'completed',
    trackId: onboarding.activeTrackName,
  })

  const nextState = { cardProgress: completedCardProgress }

  if (visitFlag && !onboarding.visitFlags?.[visitFlag]) {
    nextState.visitFlags = {
      ...(onboarding.visitFlags || {}),
      [visitFlag]: true,
    }
  }

  const onDismissBranch = currentCard?.card?.onDismiss?.activateBranch
  if (onDismissBranch) {
    const shouldActivate =
      typeof onDismissBranch.when === 'function' ? onDismissBranch.when(onBoardingState) : true

    if (shouldActivate) {
      const track = yield* getTrack(onboarding.activeTrackName)
      if (track?.steps) {
        const branchProgress = activateForkBranch(
          track.steps,
          onDismissBranch.forkId,
          onDismissBranch.branchId,
          onboarding.branchProgress || {},
        )

        if (branchProgress !== onboarding.branchProgress) {
          nextState.branchProgress = branchProgress
          nextState.cardProgress = buildCardProgressForTrack(
            track,
            completedCardProgress,
            branchProgress,
            onBoardingState,
          )
        }
      }
    }
  }

  yield put(actionSetData(nextState))
}

function* selectOnBoardingState() {
  const state = yield select((s) => s)
  return buildOnboardingCtx(state)
}

function* autorunStartTrack({ payload }) {
  const { trackId } = payload
  const onboarding = yield select(selectOnboardingState)

  if (onboarding.status === 'running') return
  if (onboarding.autorunConsumed?.[trackId]) return
  if ((onboarding.completedTracks || []).includes(trackId)) return

  const track = yield* getTrack(trackId)
  if (!track || typeof track.autorun !== 'function') return

  const onBoardingState = yield* selectOnBoardingState()
  if (!track.autorun(onBoardingState)) return

  yield put(
    actionSetData({
      autorunConsumed: {
        ...(onboarding.autorunConsumed || {}),
        [trackId]: true,
      },
    }),
  )

  yield put({ type: START_TRACK, payload: { trackId } })
}

// TODO(backend): server progress sync — uncomment when API is ready
// function* loadProgress() {
//   try {
//     const { response, error } = yield call(onboardingApi.loadProgress)
//
//     if (error || !response?.data) return
//
//     yield put(actionSetData(response.data))
//   } catch (e) {
//     // silent fail — server sync is best-effort
//   }
// }
//
// function* saveProgress({ payload }) {
//   if (!payload) return
//
//   try {
//     yield call(onboardingApi.saveProgress, payload)
//   } catch (e) {
//     // silent fail — server sync is best-effort
//   }
// }

function* askConfirmInterrupt() {
  const [confirmed, UUID] = yield call(confirmSaga, {
    title: 'onboarding::confirm_interrupt.title',
    text: i18n.t('onboarding::confirm_interrupt.text'),
    className: 'onboarding-confirm-interrupt',
    buttons: {
      cancel: i18n.t('onboarding::confirm_interrupt.cancel'),
      apply: i18n.t('onboarding::confirm_interrupt.apply'),
    },
  })

  if (UUID) {
    yield put({ type: 'MODAL/HIDE', payload: { UUID } })
  }

  return Boolean(confirmed)
}

function* confirmInterrupt() {
  if (yield call(askConfirmInterrupt)) {
    yield put({ type: INTERRUPT_TRACK })
  }
}

function* handleOnboardingModalButton(button, card) {
  const onboarding = yield select(selectOnboardingState)
  trackOnboardingButtonAnalytics(button, {
    trackId: onboarding.activeTrackName,
    cardId: card?.id,
  })

  const action = resolveButtonAction(button)
  if (action) {
    yield put(action)
  }
}

function* showOnboardingModal({ payload }) {
  const { card } = payload
  const modalUUID = uuidv4()

  yield put({
    type: 'MODAL/SHOW',
    payload: {
      modalType: 'onboarding',
      UUID: modalUUID,
      modalProps: {
        card,
        UUID: modalUUID,
        shouldCloseOnOverlayClick: false,
        shouldCloseOnEsc: false,
        breakpoints: { 0: { variant: 'default' } },
        portalClassName: 'onboarding-modal-portal',
        overlayClassName: 'onboarding-modal-overlay',
        // Онбординг не должен блокировать скролл страницы под модалкой/поповером.
        bodyOpenClassName: 'body__onboarding_open',
      },
    },
  })

  // `finally` гарантирует скрытие модалки на любом выходе из саги, включая
  // отмену через takeLatest (новый SHOW_ONBOARDING_MODAL при переходе
  // модалка→модалка или при перезапуске трека). Иначе старая модалка зависает
  // в стеке под новой карточкой.
  try {
    while (true) {
      const { payload: button } = yield take('ONBOARDING_MODAL/BUTTON_CLICK_' + modalUUID)

      if (!button) {
        continue
      }

      if (button.action === 'confirm-interrupt') {
        const onboarding = yield select(selectOnboardingState)
        trackOnboardingButtonAnalytics(button, {
          trackId: onboarding.activeTrackName,
          cardId: card?.id,
        })

        if (yield call(askConfirmInterrupt)) {
          yield put({ type: INTERRUPT_TRACK })
          break
        }

        continue
      }

      yield call(handleOnboardingModalButton, button, card)
      break
    }
  } finally {
    yield put({ type: 'MODAL/HIDE', payload: { UUID: modalUUID } })
  }
}

export default function* Sagas() {
  yield takeLatest(START_TRACK, startTrack)
  yield takeLatest(COMPLETE_TRACK, completeTrack)
  yield takeLatest(COMPLETE_BRANCH, completeBranchSaga)
  yield takeLatest(FINALIZE_TRACK, finalizeTrack)
  yield takeLatest(NEXT_STEP, nextStep)
  yield takeLatest(NAVIGATE_TO_URL, navigateToUrl)
  yield takeLatest(DISMISS_CARD, dismissCard)
  yield takeLatest(INTERRUPT_TRACK, interruptTrack)
  yield takeLatest(SHOW_ONBOARDING_MODAL, showOnboardingModal)
  yield takeLatest(CONFIRM_INTERRUPT, confirmInterrupt)
  yield takeLeading(AUTORUN_START_TRACK, autorunStartTrack)
  // Слушатели онбординга (page.listeners) на внешние redux-события.
  // Новые внешние события добавляются в этот список.
  yield takeEvery(TOP_UP_SUCCESS, handleOnboardingListenerEvent)
  // TODO(backend): server progress sync — uncomment when API is ready
  // yield takeLatest(LOAD_PROGRESS, loadProgress)
  // yield takeLatest(SAVE_PROGRESS, saveProgress)
}
